import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle2, Clock, Download, Truck } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { subscribeToOrders, formatOrderNumber, type Order, type OrderLineItem } from '../../../utils/firebase';
import { getBookDownloadUrl } from '../../../utils/downloads';
import { shopData } from './shopData';
import { bookData } from './bookData';
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function normalizeProductName(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/(\d+)/g, '').split(/[^a-z]+/).filter(Boolean).sort().join('');
}
function getItemImage(item: OrderLineItem): string | undefined {
  const pid = item.productId;
  const normalizedName = normalizeProductName(item.name);
  if (item.type === 'book') {
    const book = bookData.find(b => b.id === pid) ?? bookData.find(b => b.lookupKey && b.lookupKey === pid) ?? bookData.find(b => b.stripePriceId && b.stripePriceId === pid) ?? bookData.find(b => normalizeProductName(b.title) === normalizedName);
    return book?.coverImage;
  }
  const product = shopData.find(s => s.id === pid) ?? shopData.find(s => s.lookupKey && s.lookupKey === pid) ?? shopData.find(s => {
    const catalogName = normalizeProductName(s.name);
    return catalogName === normalizedName || catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName);
  }) ?? (normalizedName === 'omega' ? shopData.find(s => s.id === 'omega-3') : undefined);
  return product?.image;
}
function getItemShopId(item: OrderLineItem): string | undefined {
  const pid = item.productId;
  const normalizedName = normalizeProductName(item.name);
  if (item.type === 'book') return undefined;
  const product = shopData.find(s => s.id === pid) ?? shopData.find(s => s.lookupKey && s.lookupKey === pid) ?? shopData.find(s => {
    const catalogName = normalizeProductName(s.name);
    return catalogName === normalizedName || catalogName.startsWith(normalizedName) || normalizedName.startsWith(catalogName);
  }) ?? (normalizedName === 'omega' ? shopData.find(s => s.id === 'omega-3') : undefined);
  return product?.id;
}
function FulfillmentStatus({
  order
}: {
  order: Order;
}) {
  if (order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'digital_delivered') {
    return <span className="inline-flex items-center gap-2 text-sm font-bold text-tealslate" data-id="element-2069" data-mp="420n1uxujs6l">
        <CheckCircle2 className="w-4 h-4" data-id="element-2070" data-mp="43v591hqh66z" />
        Delivered
      </span>;
  }
  if (order.fulfillmentStatus === 'shipped') {
    return <span className="inline-flex items-center gap-2 text-sm font-bold text-tealslate" data-id="element-2071" data-mp="2ckimfbkkj3pz">
        <Truck className="w-4 h-4" data-id="element-2072" data-mp="28j6v91m1qafo" />
        Shipped
      </span>;
  }
  return <span className="inline-flex items-center gap-2 text-sm font-bold text-burgundy" data-id="element-2073" data-mp="23lf2y6hhomay">
      <Clock className="w-4 h-4" data-id="element-2074" data-mp="30b5zscd96ljx" />
      Processing
    </span>;
}
function DownloadButton({
  orderId
}: {
  orderId: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        url
      } = await getBookDownloadUrl(orderId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.message ?? 'Download failed. Try again.');
    } finally {
      setLoading(false);
    }
  };
  return <div data-id="element-2075" data-mp="1aykk1e0bluzl">
      <button type="button" onClick={handleDownload} disabled={loading} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-navyslate disabled:opacity-60 transition-colors inline-flex items-center gap-2 rounded-md" data-id="element-2076" data-mp="2ysxgofugb9x2">
        <Download className="w-4 h-4" data-id="element-2077" data-mp="2ur2k1ongtkpm" />
        {loading ? 'Preparing...' : 'Download'}
      </button>

      {error && <p className="text-xs text-burgundy mt-2" data-id="element-2078" data-mp="3ign5qfb06w2l">{error}</p>}
    </div>;
}
export function AccountOrderDetail() {
  const {
    orderId
  } = useParams<{
    orderId: string;
  }>();
  const {
    user,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', {
        state: {
          from: `/account/orders/${orderId}`
        },
        replace: true
      });
    }
  }, [user, authLoading, navigate, orderId]);
  useEffect(() => {
    if (!user || !orderId) return;
    setOrdersLoading(true);
    const unsubscribe = subscribeToOrders(user.uid, orders => {
      const matchingOrder = orders.find(candidate => candidate.id === orderId);
      if (matchingOrder) {
        setOrder(matchingOrder);
        setNotFound(false);
      } else {
        setOrder(null);
        setNotFound(true);
      }
      setOrdersLoading(false);
    });
    return unsubscribe;
  }, [user, orderId]);
  if (authLoading || ordersLoading || !user) {
    return <div className="w-full min-h-screen bg-white flex items-center justify-center" data-id="element-2079" data-mp="1klq0wiy81w72">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-2080" data-mp="1ft6mxkvmgz1q" />
      </div>;
  }
  if (notFound || !order) {
    return <div className="w-full min-h-screen bg-white pt-32 pb-24 px-6" data-id="element-2081" data-mp="n5u49lsl1bdp">
        <div className="max-w-4xl mx-auto" data-id="element-2082" data-mp="n3pzl1mv08cd">
          <Link to="/account" className="inline-flex items-center gap-2 text-sm font-bold text-tealslate hover:text-navyslate mb-8" data-id="element-2083" data-mp="2n8lgcmov2mch">
            <ArrowLeft className="w-4 h-4" data-id="element-2084" data-mp="2wxmt84040nm8" />
            Back to Order History
          </Link>

          <div className="bg-white border border-[#E5E5E5] rounded-md p-10 text-center" data-id="element-2085" data-mp="31wq0zw7h57xw">
            <AlertCircle className="w-10 h-10 text-burgundy mx-auto mb-4" data-id="element-2086" data-mp="31yiwqggju7r4" />
            <h1 className="text-2xl font-bold text-[#111111] mb-2" data-id="element-2087" data-mp="24asd1jabsaek">
              Order not found
            </h1>
            <p className="text-sm text-[#5A5A5A]" data-id="element-2088" data-mp="29a5127cxnniq">
              This order could not be found in your account.
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-white pt-32 pb-24 px-6" data-id="element-2089" data-mp="2ia1vkpm07glp">
      <div className="max-w-5xl mx-auto" data-id="element-2090" data-mp="3pl8zxl4mw8db">

        <Link to="/account" className="inline-flex items-center gap-2 text-sm font-bold text-tealslate hover:text-navyslate transition-colors mb-8" data-id="element-2091" data-mp="3pon1ir5h4djp">
          <ArrowLeft className="w-4 h-4" data-id="element-2092" data-mp="1hzr00bbe0m3m" />
          Back to Order History
        </Link>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8" data-id="element-2093" data-mp="1dzgvl338u3qv">
          <div data-id="element-2094" data-mp="196xgu2stl2kh">
            <p className="text-xs font-bold uppercase tracking-wider text-[#111111] mb-2" data-id="element-2095" data-mp="196e02pewok81">
              Order #{formatOrderNumber(order.id)}
            </p>

            <h1 className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-3" data-id="element-2096" data-mp="zaaxxjk20ll3">
              Order Details
            </h1>

            <p className="text-sm text-[#5A5A5A]" data-id="element-2097" data-mp="u9mxy8dukueq">
              Placed {formatDate(order.createdAt)}
            </p>
          </div>

          <FulfillmentStatus order={order} data-id="element-2098" data-mp="lajcwyv4e4xl" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2099" data-mp="lc47uplcjpvz">

          <div className="lg:col-span-2 space-y-6" data-id="element-2100" data-mp="2knnid8mat67r">

            <div className="bg-white border border-[#E5E5E5] rounded-md p-6 md:p-8" data-id="element-2101" data-mp="2kl1b7a3bksq2">
              <h2 className="text-xl font-bold text-[#111111] mb-6" data-id="element-2102" data-mp="2fp2ijaaykoju">
                Items
              </h2>

              <ul className="divide-y divide-[#E5E5E5]" data-id="element-2103" data-mp="1olavw02oli5c">
                {order.lineItems.map((item, index) => {
                const image = getItemImage(item);
                return <li key={`${item.productId}-${index}`} className="py-6 first:pt-0 last:pb-0 flex gap-6" data-id="element-2104" data-mp="1er11kcu6i9rz" data-mpi={index}>
                      {image && <div className="w-24 h-24 sm:w-32 sm:h-32 shrink-0 flex items-center justify-center" data-id="element-2105" data-mp="19u2bc5dmymz2">
                          <img src={image} alt={item.name} className="max-w-full max-h-full object-contain" data-id="element-2106" data-mp="vuj2tbgwx9m8" />
                        </div>}

                      <div className="flex-1" data-id="element-2107" data-mp="2q67khlej3qab">
                        {item.type === 'book' ? <Link to="/books" className="font-bold text-[#0056b3] hover:text-[#004494] hover:underline text-lg mb-2 block transition-colors" data-id="element-2108" data-mp="2v5apx6yf7yit">
                            {item.name}
                          </Link> : <Link to={`/shop/${getItemShopId(item) || item.productId}`} className="font-bold text-[#0056b3] hover:text-[#004494] hover:underline text-lg mb-2 block transition-colors" data-id="element-2109" data-mp="tawgskhy87jp">
                            {item.name}
                          </Link>}

                        <p className="text-sm text-[#5A5A5A] mb-1" data-id="element-2110" data-mp="xaeuav7fxgci">
                          Quantity: {item.quantity}
                        </p>

                        <p className="text-sm text-[#5A5A5A] mb-4" data-id="element-2111" data-mp="1c6cv4qsb78v2">
                          {formatPrice(item.unitPrice * item.quantity)}
                        </p>

                        {item.type === 'book' && order.status === 'paid' && <DownloadButton orderId={order.id} data-id="element-2112" data-mp="392tst8ggymtf" />}
                      </div>
                    </li>;
              })}
              </ul>
            </div>

            {order.shippingAddress?.line1 && <div className="bg-white border border-[#E5E5E5] rounded-md p-6 md:p-8" data-id="element-2113" data-mp="dycv0ph05q05">
                <h2 className="text-xl font-bold text-[#111111] mb-5" data-id="element-2114" data-mp="90kz7kd8ljqo">
                  Shipping Information
                </h2>

                <div className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2115" data-mp="16mihbgnpfn23">
                  {order.shippingAddress.recipientName && <p className="font-bold text-[#111111]" data-id="element-2116" data-mp="11r34ms1fnx79">
                      {order.shippingAddress.recipientName}
                    </p>}

                  <p data-id="element-2117" data-mp="rye4gtbc25r8">{order.shippingAddress.line1}</p>

                  {order.shippingAddress.line2 && <p data-id="element-2118" data-mp="1pfm9syp6rtv4">{order.shippingAddress.line2}</p>}

                  <p data-id="element-2119" data-mp="1kmjo1cqnhoph">
                    {order.shippingAddress.city},{' '}
                    {order.shippingAddress.state}{' '}
                    {order.shippingAddress.postalCode}
                  </p>

                  <p data-id="element-2120" data-mp="1wycdqap8awn1">{order.shippingAddress.country}</p>
                </div>

                {order.shippingAddress.deliveryInstructions && <div className="mt-6 pt-6 border-t border-[#E5E5E5]" data-id="element-2121" data-mp="26w0a152hgc7b">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A90] mb-2" data-id="element-2122" data-mp="26tx93pzskynt">
                      Delivery Instructions
                    </p>

                    <p className="text-sm text-[#5A5A5A]" data-id="element-2123" data-mp="2fvn2h1d8i7o8">
                      {order.shippingAddress.deliveryInstructions}
                    </p>
                  </div>}
              </div>}

            {order.trackingNumber && <div className="bg-white border border-[#E5E5E5] rounded-md p-6 md:p-8" data-id="element-2124" data-mp="2ulumhliw3yr1">
                <h2 className="text-xl font-bold text-[#111111] mb-4" data-id="element-2125" data-mp="2zkp1sdhnf56e">
                  Tracking
                </h2>

                <p className="text-sm text-[#5A5A5A] flex items-center gap-2" data-id="element-2126" data-mp="29w0gsyr1qsvh">
                  <Truck className="w-4 h-4" data-id="element-2127" data-mp="24xgtov0pvb1j" />
                  {order.trackingCarrier ?? 'Tracking'} ·{' '}
                  {order.trackingNumber}
                </p>
              </div>}
          </div>

          <div data-id="element-2128" data-mp="2ev37tfa6yykg">
            <div className="bg-white border border-[#E5E5E5] rounded-md p-6 sticky top-28" data-id="element-2129" data-mp="2eulfuuukb6ic">
              <h2 className="text-xl font-bold text-[#111111] mb-6" data-id="element-2130" data-mp="2sm8cwwwbwj26">
                Order Summary
              </h2>

              <div className="space-y-4 text-sm" data-id="element-2131" data-mp="2nn57vcgcmcu7">
                <div className="flex justify-between text-[#5A5A5A]" data-id="element-2132" data-mp="32l7fpyuexkh9">
                  <span data-id="element-2133" data-mp="32j4oxclfrnbh">Subtotal</span>
                  <span data-id="element-2134" data-mp="2xlkzdgjafo48">{formatPrice(order.subtotal)}</span>
                </div>

                {(order.shipping ?? 0) > 0 && <div className="flex justify-between text-[#5A5A5A]" data-id="element-2135" data-mp="187zcpjwaa2kj">
                    <span data-id="element-2136" data-mp="138mpgy1i2thf">Shipping</span>
                    <span data-id="element-2137" data-mp="139o6rk940c7l">{formatPrice(order.shipping ?? 0)}</span>
                  </div>}

                <div className="flex justify-between text-[#5A5A5A]" data-id="element-2138" data-mp="3ew8c5smv96rp">
                  <span data-id="element-2139" data-mp="3jt899e1qmz6o">Tax</span>
                  <span data-id="element-2140" data-mp="3jwmauk2kv4d2">{formatPrice(order.tax ?? 0)}</span>
                </div>

                {(order.discount ?? 0) > 0 && <div className="flex justify-between text-[#5A5A5A]" data-id="element-2141" data-mp="3a4gj9bg8moye">
                    <span data-id="element-2142" data-mp="3a2dlg58ufrj4">Discount</span>
                    <span data-id="element-2143" data-mp="2w4pbvtk886td">-{formatPrice(order.discount ?? 0)}</span>
                  </div>}

                <div className="pt-4 border-t border-[#E5E5E5] flex justify-between font-bold text-[#111111] text-base" data-id="element-2144" data-mp="307lnh0bcn2nt">
                  <span data-id="element-2145" data-mp="2m8ca08ej2mnj">Grand Total</span>
                  <span data-id="element-2146" data-mp="2r5m6u948y0le">{formatPrice(order.total)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>;
}